import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, errorText, uploadFile } from '../api/http';
import type { Me } from '../api/types';
import { Avatar } from '../components/Avatar';
import { IconBack } from '../components/Icons';
import { enablePush, isStandalone, pushState, unsubscribePush, type PushState } from '../lib/push';
import { logout, setMe, useChat } from '../store/chat';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredInstall: BeforeInstallPromptEvent | null = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredInstall = e as BeforeInstallPromptEvent;
});

const PUSH_TEXT: Record<PushState, string> = {
  enabled: 'Включены',
  disabled: 'Выключены',
  denied: 'Запрещены в настройках браузера',
  unsupported: 'Не поддерживаются этим браузером',
  'needs-install': 'На iPhone и iPad уведомления работают после установки на экран «Домой»',
  'server-disabled': 'Не настроены на сервере',
};

export function SettingsPage() {
  const nav = useNavigate();
  const me = useChat((s) => s.me)!;
  const [name, setName] = useState(me.hasName ? me.displayName : '');
  const [username, setUsername] = useState(me.username ?? '');
  // Signed in with a password from the admin (password reset): it is filled in, the user picks their own.
  const tempPassword = (useLocation().state as { tempPassword?: string } | null)?.tempPassword;
  const [pw, setPw] = useState({ current: tempPassword ?? '', next: '' });
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [push, setPush] = useState<PushState | null>(null);
  const [canInstall, setCanInstall] = useState(Boolean(deferredInstall));
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void pushState().then(setPush);
  }, []);

  const nameChanged = name.trim() !== '' && name.trim() !== (me.hasName ? me.displayName : '');
  const usernameChanged = username.trim() !== '' && username.trim().toLowerCase() !== (me.username ?? '');

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body: Record<string, string> = {};
      if (nameChanged) body.displayName = name;
      if (usernameChanged) body.username = username;
      const { user } = await api<{ user: Me }>('PATCH', '/api/users/me', body);
      setMe(user);
      setMsg('Сохранено');
    } catch (err) {
      setMsg(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api('POST', '/api/auth/change-password', { currentPassword: pw.current, newPassword: pw.next });
      setPw({ current: '', next: '' });
      setPwMsg('Пароль изменён. Другие устройства вышли из аккаунта.');
    } catch (err) {
      setPwMsg(errorText(err));
    }
  };

  const uploadAvatar = async (file: File | undefined) => {
    if (!file) return;
    setMsg('Загрузка аватара…');
    try {
      const { user } = await uploadFile<{ user: Me }>('/api/users/me/avatar', file, file.name);
      setMe(user);
      setMsg('Аватар обновлён');
    } catch (err) {
      setMsg(errorText(err));
    }
  };

  const removeAvatar = async () => {
    const { user } = await api<{ user: Me }>('DELETE', '/api/users/me/avatar');
    setMe(user);
  };

  const togglePush = async () => {
    try {
      if (push === 'enabled') {
        await unsubscribePush();
        setPush('disabled');
      } else setPush(await enablePush());
    } catch (err) {
      setMsg(err instanceof Error ? err.message : errorText(err));
    }
  };

  const install = async () => {
    if (!deferredInstall) return;
    await deferredInstall.prompt();
    deferredInstall = null;
    setCanInstall(false);
  };

  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <section className="settings">
      <header className="chat-header">
        <button className="icon-btn back" onClick={() => nav('/')} aria-label="Назад">
          <IconBack />
        </button>
        <h2>Настройки</h2>
      </header>
      <div className="settings-body">
        <div className="settings-profile">
          <Avatar user={me} size={96} />
          <div className="row-gap">
            <button className="btn" onClick={() => fileRef.current?.click()}>
              Изменить фото
            </button>
            {me.avatarUrl && (
              <button className="btn ghost" onClick={removeAvatar}>
                Удалить
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={(e) => uploadAvatar(e.target.files?.[0])} />
        </div>

        <form className="form" onSubmit={saveProfile}>
          <label>
            Имя
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={64} placeholder="Как вас зовут" />
          </label>
          <label>
            Ник
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/^@/, ''))}
              pattern="[A-Za-z][A-Za-z0-9_]{2,31}"
              title="3–32 символа: латинские буквы, цифры и _; начинается с буквы"
              placeholder="по нему вас найдут в поиске"
              autoCapitalize="none"
              autoCorrect="off"
            />
          </label>
          <button className="btn primary" disabled={saving || !(nameChanged || usernameChanged)}>
            Сохранить
          </button>
        </form>
        {msg && <p className="form-hint">{msg}</p>}

        <dl className="settings-info">
          <dt>Телефон</dt>
          <dd>{me.phone ?? '—'}</dd>
        </dl>

        <form className="form settings-section" onSubmit={changePassword}>
          <h3>Пароль</h3>
          {tempPassword && !pwMsg && (
            <p className="form-hint reset-hint">Вы вошли по паролю от администратора. Придумайте свой пароль — так его будете знать только вы.</p>
          )}
          <label>
            Текущий пароль
            <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" required />
          </label>
          <label>
            Новый пароль
            <input
              type="password"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              minLength={8}
              autoComplete="new-password"
              required
              autoFocus={!!tempPassword}
            />
          </label>
          <button className="btn">Сменить пароль</button>
          {pwMsg && <p className="form-hint">{pwMsg}</p>}
        </form>

        <div className="settings-section">
          <h3>Уведомления</h3>
          <p className="muted">{push ? PUSH_TEXT[push] : '…'}</p>
          {(push === 'enabled' || push === 'disabled') && (
            <button className="btn" onClick={togglePush}>
              {push === 'enabled' ? 'Выключить' : 'Включить уведомления'}
            </button>
          )}
          <p className="muted small">
            Уведомления о звонках при закрытом приложении доставляются, но принять звонок в фоне можно не на всех устройствах.
          </p>
        </div>

        {!isStandalone() && (
          <div className="settings-section">
            <h3>Установка</h3>
            {canInstall ? (
              <button className="btn" onClick={install}>
                Установить приложение
              </button>
            ) : ios ? (
              <p className="muted">В Safari нажмите «Поделиться» → «На экран „Домой“».</p>
            ) : (
              <p className="muted">Используйте пункт «Установить приложение» в меню браузера.</p>
            )}
          </div>
        )}

        <button className="btn danger" onClick={() => void logout().then(() => nav('/login', { replace: true }))}>
          Выйти из аккаунта
        </button>
      </div>
    </section>
  );
}
